import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CircleHelp, LayoutGrid, LayoutList, Loader2, Plus, BadgeCheck, Sparkles, TriangleAlert } from 'lucide-react';

import { CttqListHealthBanner } from '../components/ttq/CttqHealthBanner';
import { SectionCard } from '../components/ttq/SectionCard';
import { useActiveSubject } from '../context/ActiveSubjectContext';
import { useAuth } from '../context/AuthContext';
import { accountUserProfileService } from '../services/accountUserProfileService';
import { knowledgeService } from '../services/knowledgeService';
import {
  getLearningSequenceSettings,
  updateLearningSequenceSettings,
} from '../services/reviewService';
import {
  SUBJECT_CONFIG,
} from '../utils/subjectConfig';
import {
  countTtqSessionParticles,
  formatTtqSessionCompressionLine,
  listTtqSessionOriginalParticleIds,
  resolveTtqSessionIpttqCount,
  resolveTtqSessionTitle,
  ttqSessionStatusLabel,
} from '../utils/ttqDisplay';
import { isTtqSubjectEnabled, resolveTtqDockSubject, ttqService, type TtqSession, type TtqSubjectCode } from '../services/ttqService';
import { ttqMembershipPressureService } from '../services/ttqMembershipPressureService';
import { ipttqService } from '../services/ipttqService';
import { peekTtqSessionList, setTtqSessionList, invalidateTtqSessionList } from '../services/ttqSessionListCache';
import { IskPreviewStack, IskPreviewStackEmpty } from '../features/ttqib/IskPreviewStack';
import { collectIskPreviewsFromRecords } from '../features/ttqib/IskToolbarThumb';

const LAYOUT_STORAGE_KEY = 'psycheflow.ttqListLayout';
type ListLayout = 'list' | 'card';

function readStoredLayout(): ListLayout {
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    return raw === 'card' ? 'card' : 'list';
  } catch {
    return 'list';
  }
}

function sessionIskPreviews(session: TtqSession) {
  if (session.iskPreviews?.length) return session.iskPreviews;
  if (session.iskPreview) return [session.iskPreview];
  return [];
}

async function enrichSessionsWithIskPreviews(rows: TtqSession[]): Promise<TtqSession[]> {
  // Server listSessions already embeds iskPreviews (incl. []). Only legacy payloads lack the field.
  const need = rows.filter((session) => (
    session.iskPreviews === undefined
    && !session.iskPreview
    && (session.ipttqCount || 0) > 0
  ));
  if (!need.length) return rows;
  const enriched = await Promise.all(need.map(async (session) => {
    try {
      const payload = await ipttqService.listByTtqa(session.id);
      const previews = collectIskPreviewsFromRecords(payload.records || []);
      if (!previews.length) return session;
      return {
        ...session,
        iskPreviews: previews,
        iskPreview: previews[0] || null,
      };
    } catch {
      return session;
    }
  }));
  const byId = new Map(enriched.map((session) => [session.id, session]));
  return rows.map((session) => byId.get(session.id) || session);
}

function isEmptyTtqSession(session: TtqSession): boolean {
  return countTtqSessionParticles(session).total === 0;
}

/** 跨品题会话去重后的原始知识颗粒数（仅计已有真正浓缩结果的会话） */
function countUniqueOriginalParticles(sessions: TtqSession[]): number {
  const ids = new Set<string>();
  for (const session of sessions) {
    if (!sessionHasCompleteGeneratedIpttq(session)) continue;
    for (const id of listTtqSessionOriginalParticleIds(session)) {
      ids.add(id);
    }
  }
  return ids.size;
}

function sessionHasCompleteGeneratedIpttq(session: TtqSession): boolean {
  if (typeof session.hasCompleteGeneratedIpttq === 'boolean') {
    return session.hasCompleteGeneratedIpttq;
  }
  const records = Array.isArray(session.ipttqRecords) ? session.ipttqRecords : [];
  return records.some((record) => {
    if (!record) return false;
    if (record.bankMode === 'session_originals_ref') return false;
    if (record.lifecycle === 'archived') return false;
    const caps = record.skill?.implicitCapabilities || [];
    const lineage = String(record.skill?.thinkingLineage || '').trim();
    const draftLineage = !lineage
      || lineage.includes('待根据')
      || lineage.includes('请稍后重试');
    const hasSkillBody = caps.length > 0 || (!draftLineage && lineage.length >= 12);
    if (!hasSkillBody) return false;
    const bankCount = typeof record.effectiveBankCount === 'number'
      ? record.effectiveBankCount
      : (record.bankParticleIds || []).length;
    return record.lifecycle === 'active' || bankCount > 0 || caps.length > 0;
  });
}

/**
 * 复习负担减少比例（展示用）：
 * 去重原始 KP（已被真正浓缩结果覆盖） / 当前学科全部 KP
 */
function computeReviewBurdenReductionPercent(
  coveredOriginalCount: number,
  subjectParticleTotal: number | null,
): number | null {
  if (subjectParticleTotal == null || subjectParticleTotal <= 0) return null;
  if (coveredOriginalCount <= 0) return 0;
  return Math.min(100, Math.round((coveredOriginalCount / subjectParticleTotal) * 1000) / 10);
}

/** 尚未被真正浓缩结果覆盖的学科颗粒数 */
function computeUnusedParticleCount(
  coveredOriginalCount: number,
  subjectParticleTotal: number | null,
): number | null {
  if (subjectParticleTotal == null || subjectParticleTotal <= 0) return null;
  return Math.max(0, subjectParticleTotal - Math.max(0, coveredOriginalCount));
}

/**
 * 若剩余颗粒全部完成真正品题浓缩，复习负担大约再减少的比例：
 * (M − N) / M = 1 − N/M
 */
function computeProjectedRemainingBurdenReductionPercent(
  coveredOriginalCount: number,
  subjectParticleTotal: number | null,
): number | null {
  const unused = computeUnusedParticleCount(coveredOriginalCount, subjectParticleTotal);
  if (unused == null || subjectParticleTotal == null) return null;
  return computeReviewBurdenReductionPercent(unused, subjectParticleTotal);
}

export function TtqaListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSubject: subject } = useActiveSubject();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<TtqSession[]>([]);
  const [ipttqValveOn, setIpttqValveOn] = useState(false);
  const [subjectParticleTotal, setSubjectParticleTotal] = useState<number | null>(null);
  const [burdenHelpOpen, setBurdenHelpOpen] = useState(false);
  const [subtitleHelpOpen, setSubtitleHelpOpen] = useState(false);
  const [layout, setLayout] = useState<ListLayout>(() => readStoredLayout());
  const [pressurePendingCount, setPressurePendingCount] = useState(0);

  const ttqSubject: TtqSubjectCode | null = isTtqSubjectEnabled(subject)
    ? resolveTtqDockSubject(subject)
    : null;
  const subjectLabel = SUBJECT_CONFIG[subject]?.label ?? subject;

  const emptySession = useMemo(
    () => sessions.find((session) => isEmptyTtqSession(session)) ?? null,
    [sessions],
  );
  const createBlocked = Boolean(emptySession);

  const coveredOriginalCount = useMemo(
    () => countUniqueOriginalParticles(sessions),
    [sessions],
  );
  const burdenReductionPercent = useMemo(
    () => computeReviewBurdenReductionPercent(coveredOriginalCount, subjectParticleTotal),
    [coveredOriginalCount, subjectParticleTotal],
  );
  const unusedParticleCount = useMemo(
    () => computeUnusedParticleCount(coveredOriginalCount, subjectParticleTotal),
    [coveredOriginalCount, subjectParticleTotal],
  );
  const projectedRemainingBurdenPercent = useMemo(
    () => computeProjectedRemainingBurdenReductionPercent(coveredOriginalCount, subjectParticleTotal),
    [coveredOriginalCount, subjectParticleTotal],
  );

  const load = useCallback(async (options?: { forceRefresh?: boolean }) => {
    if (!ttqSubject) {
      setSessions([]);
      setSubjectParticleTotal(null);
      setLoading(false);
      return;
    }
    const userId = String(user?.id || '').trim();

    const refreshValve = async () => {
      if (!userId) {
        setIpttqValveOn(false);
        return;
      }
      let valve = Boolean(getLearningSequenceSettings(userId).includeIpttqInReviewSequence);
      try {
        const remote = await accountUserProfileService.getLearningSequence(userId);
        if (typeof remote.includeIpttqInReviewSequence === 'boolean') {
          valve = remote.includeIpttqInReviewSequence;
          updateLearningSequenceSettings(userId, {
            includeIpttqInReviewSequence: valve,
          });
        }
      } catch {
        // 本地设置兜底
      }
      setIpttqValveOn(valve);
    };

    if (!options?.forceRefresh && userId) {
      const cached = peekTtqSessionList(userId, ttqSubject);
      if (cached) {
        setSessions(cached.sessions);
        setSubjectParticleTotal(cached.subjectParticleTotal);
        setLoading(false);
        void refreshValve();
        return;
      }
    }
    setLoading(true);
    setError(null);
    try {
      await refreshValve();

      const [nextSessions, particlePage] = await Promise.all([
        ttqService.listSessions(ttqSubject),
        userId
          ? knowledgeService.listKnowledgeParticles({
            userId,
            subject: ttqSubject,
            page: 1,
            pageSize: 1,
          }).catch(() => null)
          : Promise.resolve(null),
      ]);
      const withIsk = await enrichSessionsWithIskPreviews(nextSessions);
      setSessions(withIsk);
      const total = particlePage?.pagination?.total;
      const nextTotal = typeof total === 'number' && Number.isFinite(total) ? total : null;
      setSubjectParticleTotal(nextTotal);
      if (userId) {
        setTtqSessionList(userId, ttqSubject, withIsk, nextTotal);
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '加载品题列表失败');
    } finally {
      setLoading(false);
    }
  }, [ttqSubject, user?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const refreshPressureBadge = useCallback(async () => {
    if (!ipttqValveOn || !ttqSubject) {
      setPressurePendingCount(0);
      return;
    }
    try {
      // 仅刷红点；孤儿 reconcile 由 AppWorker heartbeat 负责。
      const rows = await ttqMembershipPressureService.listRecommendations('recommended');
      setPressurePendingCount(rows.filter((row) => row.subjectCode === ttqSubject).length);
    } catch (pressureError) {
      console.warn('[ttqa] membership pressure badge failed', pressureError);
    }
  }, [ipttqValveOn, ttqSubject]);

  useEffect(() => {
    void refreshPressureBadge();
  }, [refreshPressureBadge]);

  const handleCreate = async () => {
    if (!ttqSubject) return;
    if (emptySession) {
      navigate(`/ttq/${emptySession.id}`);
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const session = await ttqService.createSession(ttqSubject);
      invalidateTtqSessionList(user?.id, ttqSubject);
      navigate(`/ttq/${session.id}`);
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : '创建品题失败');
    } finally {
      setCreating(false);
    }
  };

  if (!ttqSubject) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <h1 className="text-2xl font-semibold text-ink">品题</h1>
        <SectionCard tone="muted">
          <p className="text-sm text-muted">当前学科暂未开放品题。请在顶部导航切换到数学或语文。</p>
        </SectionCard>
      </div>
    );
  }

  return (
    <div className={`mx-auto space-y-4 ${layout === 'card' ? 'max-w-5xl' : 'max-w-3xl'}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink">{subjectLabel}品题</h1>
          <div className="mt-1 flex items-center gap-2">
            <p className="text-sm text-muted">将较为相似的题目融合起来降低复习负担</p>
            <div className="relative">
              <button
                type="button"
                className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-surface px-2 text-xs font-semibold text-primary hover:bg-primary/10"
                aria-label="怎么做：品题步骤说明"
                aria-expanded={subtitleHelpOpen}
                onClick={() => setSubtitleHelpOpen((open) => !open)}
              >
                怎么做？
                <CircleHelp size={14} />
              </button>
              {subtitleHelpOpen ? (
                <>
                  <button
                    type="button"
                    className="fixed inset-0 z-40 cursor-default bg-transparent"
                    aria-label="关闭说明"
                    onClick={() => setSubtitleHelpOpen(false)}
                  />
                  <div className="absolute left-0 top-full z-50 mt-2 w-[min(24rem,calc(100vw-2rem))] rounded-xl border border-border bg-surface p-4 shadow-lg">
                    <p className="text-base font-semibold text-ink">怎么做</p>
                    <ol className="mt-3 space-y-2 text-sm font-medium leading-6 text-ink">
                      <li>第一步：新建品题</li>
                      <li>第二步：将相似的知识颗粒加入品题。知识颗粒的相似，表现为题目解法相似、运用基本思想相似；例如都用了整体思想、枚举思想，都要用方程来解题，等等。</li>
                      <li>第三步：新建品题浓缩结果</li>
                      <li>第四步：更新品题结果</li>
                      <li>第五步：构建诱导题库</li>
                    </ol>
                    <p className="mt-3 border-t border-border pt-3 text-sm leading-6 text-muted">
                      此时诱导题库就会将知识颗粒浓缩成更综合的题目，替换原有题目，显著减少复习负担。
                    </p>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="relative inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-surface px-3 text-sm font-medium text-ink hover:bg-black/[0.04]"
            aria-label="入围推荐"
            title="入围推荐"
            onClick={() => navigate('/ttq/recommendations')}
          >
            <Sparkles size={16} />
            <span className="hidden sm:inline">入围推荐</span>
            {pressurePendingCount > 0 ? (
              <span
                className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-danger ring-2 ring-canvas"
                aria-hidden
              />
            ) : null}
          </button>
          <div className="inline-flex overflow-hidden rounded-lg border border-border">
            <button
              type="button"
              className={
                'inline-flex h-9 w-9 items-center justify-center '
                + (layout === 'list' ? 'bg-primary/10 text-primary' : 'text-muted hover:bg-black/[0.04]')
              }
              aria-label="列表式"
              aria-pressed={layout === 'list'}
              title="列表式"
              onClick={() => {
                setLayout('list');
                try { localStorage.setItem(LAYOUT_STORAGE_KEY, 'list'); } catch { /* ignore */ }
              }}
            >
              <LayoutList size={16} />
            </button>
            <button
              type="button"
              className={
                'inline-flex h-9 w-9 items-center justify-center border-l border-border '
                + (layout === 'card' ? 'bg-primary/10 text-primary' : 'text-muted hover:bg-black/[0.04]')
              }
              aria-label="卡片式"
              aria-pressed={layout === 'card'}
              title="卡片式"
              onClick={() => {
                setLayout('card');
                try { localStorage.setItem(LAYOUT_STORAGE_KEY, 'card'); } catch { /* ignore */ }
              }}
            >
              <LayoutGrid size={16} />
            </button>
          </div>
          <button
            type="button"
            className={
              'inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50'
              + (createBlocked ? ' opacity-50' : '')
            }
            disabled={creating || loading}
            aria-disabled={createBlocked || creating || loading}
            title={createBlocked ? '已有空的新品题可用' : '新建品题'}
            onClick={() => { void handleCreate(); }}
          >
            {creating ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
            新建
          </button>
        </div>
      </div>

      <SectionCard tone="accent">
        {loading ? (
          <p className="text-sm text-muted">正在加载…</p>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-5">
              <div className="min-w-0 space-y-1.5 sm:border-r sm:border-primary/15 sm:pr-4">
                <p className="text-xs font-medium uppercase tracking-wide text-muted">
                  品题记录
                </p>
                <p className="text-2xl font-semibold tabular-nums text-ink">
                  {sessions.length}
                  <span className="ml-1.5 text-sm font-normal text-muted">条</span>
                </p>
              </div>

              <div className="min-w-0 space-y-1.5 sm:border-r sm:border-primary/15 sm:pr-4">
                <div className="flex items-center gap-1.5">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted">
                    复习负担减少
                  </p>
                  <div className="relative">
                    <button
                      type="button"
                      className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted hover:bg-primary/10 hover:text-primary"
                      aria-label="复习负担减少说明"
                      aria-expanded={burdenHelpOpen}
                      onClick={() => setBurdenHelpOpen((open) => !open)}
                    >
                      <CircleHelp size={14} />
                    </button>
                    {burdenHelpOpen ? (
                      <>
                        <button
                          type="button"
                          className="fixed inset-0 z-40 cursor-default bg-transparent"
                          aria-label="关闭说明"
                          onClick={() => setBurdenHelpOpen(false)}
                        />
                        <div
                          role="dialog"
                          aria-label="复习负担减少说明"
                          className="absolute left-0 top-full z-50 mt-1.5 w-80 max-w-[min(20rem,calc(100vw-2rem))] space-y-2 rounded-xl border border-border bg-surface p-3 text-left shadow-lg"
                        >
                          <p className="text-sm leading-snug text-ink">
                            {coveredOriginalCount} 个原始知识颗粒已被真正品题浓缩结果覆盖
                          </p>
                          {subjectParticleTotal != null ? (
                            <p className="text-xs text-muted">
                              当前学科共 {subjectParticleTotal} 个知识颗粒；尚有{' '}
                              {unusedParticleCount ?? 0} 个未被真正浓缩结果覆盖
                            </p>
                          ) : (
                            <p className="text-xs text-muted">
                              需学科知识颗粒总量后计算。
                            </p>
                          )}
                          {burdenReductionPercent != null ? (
                            <p className="text-xs leading-snug text-muted">
                              当前减负 ≈ 已被真正浓缩覆盖的原始颗粒 ÷ 学科全部颗粒；完成后约减负 ≈ 1 − 已覆盖/总量
                            </p>
                          ) : null}
                          <p className="border-t border-border/70 pt-2 text-xs leading-relaxed text-muted">
                            更多的品题可以帮助学习者将大量的学习内容，压缩为更少的学习内容，通过分类构造浓缩综合题目，一方面可以锻炼学习者归纳总结学习内容的能力，另一方面可以在品题归纳的过程中，构造品题浓缩结果，替代原始的复习题目，进而针对浓缩综合题目安排复习，大大降低复习负担。负担统计只计入已有真正品题浓缩结果（非空壳草稿）的品题。
                          </p>
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
                {burdenReductionPercent != null ? (
                  <p className="text-2xl font-semibold tabular-nums leading-none text-primary sm:text-3xl">
                    {burdenReductionPercent}
                    <span className="ml-0.5 text-lg sm:text-xl">%</span>
                  </p>
                ) : (
                  <p className="text-sm text-muted">—</p>
                )}
              </div>

              <div className="min-w-0 space-y-1.5 sm:border-r sm:border-primary/15 sm:pr-4">
                <p className="text-xs font-medium uppercase tracking-wide text-muted">
                  尚未覆盖
                </p>
                {unusedParticleCount != null ? (
                  <p className="text-2xl font-semibold tabular-nums text-ink">
                    {unusedParticleCount}
                    <span className="ml-1.5 text-sm font-normal text-muted">个</span>
                  </p>
                ) : (
                  <p className="text-sm text-muted">—</p>
                )}
              </div>

              <div className="min-w-0 space-y-1.5">
                <p className="text-xs font-medium uppercase tracking-wide text-muted">
                  完成后约减负
                </p>
                {projectedRemainingBurdenPercent != null ? (
                  <p className="text-2xl font-semibold tabular-nums leading-none text-primary sm:text-3xl">
                    {projectedRemainingBurdenPercent}
                    <span className="ml-0.5 text-lg sm:text-xl">%</span>
                  </p>
                ) : (
                  <p className="text-sm text-muted">—</p>
                )}
              </div>
            </div>

            {!ipttqValveOn ? (
              <p className="mt-0 flex items-start gap-2 border-t border-amber-500/25 pt-2.5 text-xs font-medium leading-5 text-amber-700">
                <TriangleAlert size={14} className="mt-0.5 shrink-0 text-amber-600" aria-hidden />
                <span>学习设置中「品题加入复习列」未开启时，复习序列不会用浓缩结果替换原题。</span>
              </p>
            ) : null}
          </div>
        )}
      </SectionCard>

      <CttqListHealthBanner subject={ttqSubject} />

      {error ? <p className="text-sm text-danger">{error}</p> : null}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-primary" size={28} />
        </div>
      ) : null}

      {!loading && sessions.length === 0 ? (
        <SectionCard title="开始第一次品题" tone="muted">
          <p className="text-sm text-muted">
            点击「新建」创建品题活动，添加知识颗粒后系统将自动提取隐式能力包；≥2 颗粒后可做聚合与收敛品题。
          </p>
        </SectionCard>
      ) : null}

      <div className={layout === 'card' ? 'grid grid-cols-2 gap-3 sm:grid-cols-3' : 'space-y-3'}>
        {sessions.map((session) => {
          const counts = countTtqSessionParticles(session);
          const ipttqCount = resolveTtqSessionIpttqCount(session);
          const previews = sessionIskPreviews(session);
          const title = resolveTtqSessionTitle(session);

          if (layout === 'card') {
            return (
              <Link
                key={session.id}
                to={`/ttq/${session.id}`}
                className="flex flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-card transition hover:border-primary/30 hover:shadow-md"
              >
                <div className="w-full overflow-hidden bg-canvas/60">
                  {previews.length ? (
                    <IskPreviewStack
                      fillWidth
                      previews={previews}
                      altPrefix={title}
                    />
                  ) : (
                    <IskPreviewStackEmpty fillWidth />
                  )}
                </div>
                <div className="space-y-1 border-t border-border/70 px-3 py-2.5">
                  <h2 className="text-sm font-semibold leading-snug text-ink">{title}</h2>
                  <p className="text-[11px] leading-4 text-muted">
                    {formatTtqSessionCompressionLine({
                      particleTotal: counts.total,
                      ipttqCount,
                    })}
                    {session.ipttqToolbarReady ? (
                      <BadgeCheck
                        size={13}
                        className="ml-1 inline-block align-[-2px] text-success"
                        aria-label="品题浓缩结果已就绪"
                      />
                    ) : null}
                    {' · '}
                    {ttqSessionStatusLabel(session.status)}
                    {session.aggregationAvailable ? ' · 可聚合' : ''}
                  </p>
                </div>
              </Link>
            );
          }

          return (
            <Link
              key={session.id}
              to={`/ttq/${session.id}`}
              className="flex items-center gap-3 rounded-2xl border border-border bg-surface p-4 shadow-card transition hover:border-primary/30 hover:shadow-md sm:p-5"
            >
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-ink">{title}</h2>
                <p className="mt-1 text-sm leading-5 text-muted">
                  {formatTtqSessionCompressionLine({
                    particleTotal: counts.total,
                    ipttqCount,
                  })}
                  {session.ipttqToolbarReady ? (
                    <BadgeCheck
                      size={14}
                      className="ml-1 inline-block align-[-2px] text-success"
                      aria-label="品题浓缩结果已就绪"
                    />
                  ) : null}
                  {' · '}
                  {ttqSessionStatusLabel(session.status)}
                  {session.aggregationAvailable ? ' · 可聚合' : ''}
                </p>
                {ipttqValveOn && counts.original > 0 ? (
                  <p className="mt-1.5 text-sm leading-snug text-ink/80">
                    {counts.original} 个原始知识颗粒将被品题浓缩结果取代后加入复习，降低复习负担。
                  </p>
                ) : null}
                {session.summary ? (
                  <p className="mt-2 line-clamp-2 text-sm text-ink">{session.summary}</p>
                ) : null}
              </div>
              <IskPreviewStack previews={previews} size={64} altPrefix={title} />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
